import { Injectable } from '@angular/core';
import { Store } from '@ngxs/store';
import { SessionState, ShowUpdatedTermsNotification, StartupService } from '@platform-frontend/account';
import { Observable, of } from 'rxjs';
import { filter, switchMap, take } from 'rxjs/operators';
import { ShoppingCartState } from '../shopping-cart/shopping-cart.state';
import { FetchAndHandleStartupConfiguration, PersistAndCleanupStartupTempData } from '../startup';
import { PrepareFlow, SetFlow, StartFlow } from './flow.actions';
import { AppFlow } from './model/app-flow';

@Injectable({
  providedIn: 'root'
})
export class FlowStartupService {
  constructor(private startupService: StartupService, private store: Store) {}

  getStartupObservable<T extends AppFlow>(flow: T, preventStartFlow: () => Observable<boolean>): Observable<unknown> {
    return this.store.selectSnapshot(SessionState.isSignedIn)
      ? this.getBaseStartupObservable(flow, () => this.prepareAndStartFlow(preventStartFlow), false)
      : of(null);
  }

  private prepareAndStartFlow(preventStartFlow: () => Observable<boolean>): Observable<unknown> {
    return this.store.dispatch(new PrepareFlow()).pipe(
      switchMap(() => preventStartFlow()),
      filter(_preventStartFlow => !_preventStartFlow),
      switchMap(() => this.store.dispatch(new StartFlow())),
      switchMap(() => this.store.dispatch(new ShowUpdatedTermsNotification()))
    );
  }

  getBaseStartupObservable<T extends AppFlow>(
    flow: T,
    getStartupActions: () => Observable<unknown> = () => of(null),
    continueAfterSignOut: boolean = true
  ): Observable<unknown> {
    // Flow must be set before calling the startup service because the flow is indirectly used in the platform's startup actions via redirect anchors
    return this.store
      .dispatch(new SetFlow(flow))
      .pipe(
        switchMap(() =>
          this.startupService.getStartupObservable(() =>
            this.getStartupActionsWithCheckoutInitialization(flow, () => getStartupActions(), continueAfterSignOut)
          )
        )
      );
  }

  private getStartupActionsWithCheckoutInitialization(
    flow: AppFlow,
    getStartupActions: () => Observable<unknown>,
    continueAfterSignOut: boolean = true
  ): Observable<unknown> {
    return this.store.dispatch(new PersistAndCleanupStartupTempData(flow.startParams.metadataId)).pipe(
      filter(() => continueAfterSignOut || this.store.selectSnapshot(SessionState.isSignedIn)),
      switchMap(() => this.store.dispatch(new FetchAndHandleStartupConfiguration(flow))),
      switchMap(() => getStartupActions()),
      switchMap(() => this.store.select(ShoppingCartState.isPricingInformationLoading)),
      filter(isLoading => !isLoading),
      take(1)
    );
  }
}
